import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  itemWidth,
  actionsWidth
} from '~/modules/common/charts/timeline/calculations';
import {
  usePopperHandlers,
  ResourceChartTotalPopper
} from '~/modules/resourcing/components/ResourceChartTotalPopper';
import { useResourcingChartToolbarContext } from '~/modules/projects/resourcing-plan/hooks';
import ResourceRequestChartFooterRow, {
  PeriodTotalsChartFooterRow
} from '~/modules/projects/resourcing-plan/ResourceRequestChart/components/ResourceRequestChartFooterRow';
import { useProjectContext } from '~/modules/resourcing/common/contexts';
import TotalsFooterPopoverCell from '../TotalsFooterPopoverCell/TotalsFooterPopoverCell';

const useLegacyGrandTotalStyles = makeStyles(theme => ({
  root: {
    backgroundColor: 'unset',
    borderBottom: 'none'
  },
  totalLabel: {
    borderRight: 'none',
    fontWeight: theme.typography.fontWeightBold
  }
}));

const useGrandTotalStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.paper
  },
  itemContainer: {
    backgroundColor: theme.palette.background.paper
  },
  totalContainer: {
    backgroundColor: theme.palette.background.paper
  },
  timeContainerYear: {
    backgroundImage: 'none'
  },
  timeContainerQuarter: {
    backgroundImage: 'none'
  },
  timeContainerMonth: {
    backgroundImage: 'none'
  },
  timeContainerWeek: {
    backgroundImage: 'none'
  },
  timeContainerDay: {
    backgroundImage: 'none'
  },
  totalLabel: {
    fontWeight: theme.typography.fontWeightBold
  }
}));

const leftComponentWidth = actionsWidth + itemWidth * 2;
const EMPTY_PERIOD_TOTALS = [];

export const ResourcePlanGrandTotal = ({
  allocationsTotals,
  resourceRequestsTotals,
  loading,
  allocationPeriodTotals = EMPTY_PERIOD_TOTALS,
  resourceRequestPeriodTotals = EMPTY_PERIOD_TOTALS
}) => {
  const { project } = useProjectContext();
  const grandTotalClasses = useGrandTotalStyles();
  const legacyGrandTotalClasses = useLegacyGrandTotalStyles();
  const {
    scale,
    chartDates,
    isShowColumnarTotalsEnabled
  } = useResourcingChartToolbarContext();

  const grandTotalPeriodTotals = useMemo(
    () =>
      Object.values(
        [...allocationPeriodTotals, ...resourceRequestPeriodTotals].reduce(
          (acc, { dateRange, totalHours }) => {
            const key = dateRange.startDate;

            if (!acc[key]) acc[key] = { dateRange, totalHours: 0 };
            acc[key].totalHours += totalHours;

            return acc;
          },
          {}
        )
      ),
    [allocationPeriodTotals, resourceRequestPeriodTotals]
  );

  const canViewCostData = project.permittedActionUris.includes(
    'urn:replicon:project-action:view-cost-data'
  );

  const {
    anchorEl,
    onMenuClose,
    onMenuClick,
    handleKeyDown
  } = usePopperHandlers();

  const totalPopoverCell = (
    <TotalsFooterPopoverCell
      allocationsTotals={allocationsTotals}
      resourceRequestsTotals={resourceRequestsTotals}
      handleClick={onMenuClick}
      handleKeyDown={handleKeyDown}
      onClickAway={onMenuClose}
      canViewCostData={canViewCostData}
      loading={loading}
    >
      <ResourceChartTotalPopper
        id="resource-grand-total-popper"
        anchorEl={anchorEl}
        onClose={onMenuClose}
        requestedHours={resourceRequestsTotals.totalHours}
        requestedCost={resourceRequestsTotals.totalCost}
        allocatedHours={allocationsTotals.totalHours}
        allocatedCost={allocationsTotals.totalCost}
        viewCosts={canViewCostData}
        viewAllocation
        viewDifference={false}
        viewGrandTotal
      />
    </TotalsFooterPopoverCell>
  );

  return isShowColumnarTotalsEnabled ? (
    <PeriodTotalsChartFooterRow
      classes={grandTotalClasses}
      scale={scale}
      chartDates={chartDates}
      totalLabel="resourceRequestChartFooter.grandTotal"
      leftComponentWidth={leftComponentWidth}
      periodTotals={grandTotalPeriodTotals}
      periodTotalsLoading={loading}
      qeIdTag="GrandTotalPeriod"
    >
      {totalPopoverCell}
    </PeriodTotalsChartFooterRow>
  ) : (
    <ResourceRequestChartFooterRow
      classes={legacyGrandTotalClasses}
      scale={scale}
      scaleItemCount={chartDates.length}
      totalLabel="resourceRequestChartFooter.grandTotal"
      leftComponentWidth={leftComponentWidth}
    >
      {totalPopoverCell}
    </ResourceRequestChartFooterRow>
  );
};

ResourcePlanGrandTotal.propTypes = {
  allocationsTotals: PropTypes.object,
  resourceRequestsTotals: PropTypes.object,
  loading: PropTypes.bool,
  allocationPeriodTotals: PropTypes.array,
  resourceRequestPeriodTotals: PropTypes.array
};

export default ResourcePlanGrandTotal;
